import React, { type ChangeEvent } from 'react';
import { Field } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import InlineErrorMessage from '@atlassian/jira-project-settings-apps-common/src/inline-error-message/view.tsx';
import type { FieldConfigurationComponentProps } from '../../../../types.tsx';
import type { AssociatedField } from '../../../../types/layout-items.tsx';
import type { ValidationError } from '../../../../types/validation.tsx';
import { POSITION_FIELD_DEFAULT_VALUE_INPUT } from '../../../../utils/validation/constants.tsx';
import { AkFieldWrapper, FieldConfigurationDefaultValue } from '../../styled.tsx';
import messages from './messages.tsx';

export type Props = FieldConfigurationComponentProps;

const onDefaultValueChange =
	(field: AssociatedField, onUpdate: (updatedField: AssociatedField) => void) =>
	(e: ChangeEvent<HTMLInputElement>) =>
		onUpdate({
			...field,
			defaultValue: e.target.value === '' ? null : e.target.value,
		});

type FilteredValidationError = ValidationError & {
	position: typeof POSITION_FIELD_DEFAULT_VALUE_INPUT;
};

function isRelevantError(e: ValidationError): e is FilteredValidationError {
	return e.position === POSITION_FIELD_DEFAULT_VALUE_INPUT;
}

function getFilteredErrors(errors: ReadonlyArray<ValidationError>): FilteredValidationError[] {
	return errors.filter(isRelevantError);
}

const DefaultValueErrors = ({
	filteredErrors,
	inputId,
}: {
	filteredErrors: ReadonlyArray<FilteredValidationError>;
	inputId: string;
}) => {
	const intl = useIntl();
	if (filteredErrors.length === 0) {
		return null;
	}
	return (
		<label htmlFor={inputId}>
			{filteredErrors.map((e) => (
				<InlineErrorMessage
					key={e.messageDescriptor.id}
					message={intl.formatMessage(e.messageDescriptor, e.values)}
				/>
			))}
		</label>
	);
};

function refineDefaultValue(defaultValue: unknown): string {
	return typeof defaultValue === 'string' ? defaultValue : '';
}

export const NumberFieldConfiguration = ({
	field,
	onUpdate,
	fieldErrors,
	tooltipDelay,
	testId,
}: Props) => {
	const intl = useIntl();
	const isModifyDefaultValueDisabled = !field.operations.canModifyDefaultValue;

	const defaultValue: string = refineDefaultValue(field.defaultValue);
	const inputId = `${field.key}-default-value`;
	const filteredErrors: FilteredValidationError[] = getFilteredErrors(fieldErrors);
	const isInvalid: boolean = filteredErrors.length > 0;
	return (
		<FieldConfigurationDefaultValue>
			<AkFieldWrapper>
				<Field
					name={`${field.key}-default-value`}
					label={intl.formatMessage(messages.numberDefaultValueLabel)}
				>
					{({ fieldProps }) => (
						<Tooltip
							content={
								isModifyDefaultValueDisabled
									? intl.formatMessage(messages.numberDefaultValueDisabledTooltip)
									: null
							}
							delay={tooltipDelay}
						>
							<Textfield
								{...fieldProps}
								isDisabled={isModifyDefaultValueDisabled}
								type="number"
								value={defaultValue}
								placeholder={intl.formatMessage(messages.numberDefaultValuePlaceholder)}
								onChange={onDefaultValueChange(field, onUpdate)}
								isInvalid={isInvalid}
								aria-invalid={isInvalid}
								testId={testId}
							/>
						</Tooltip>
					)}
				</Field>
				<DefaultValueErrors filteredErrors={filteredErrors} inputId={inputId} />
			</AkFieldWrapper>
		</FieldConfigurationDefaultValue>
	);
};
